// 认证相关 API 服务
import { apiClient, API_ENDPOINTS } from './config.js';

// 用户状态管理
class AuthService {
  constructor() {
    this.currentUser = null;
    this.isAuthenticated = false;
    this.userRole = 'user';
    
    // 初始化时检查本地存储的 token
    this.initializeAuth();
  }
  
  // 初始化认证状态
  async initializeAuth() {
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        await this.getCurrentUser();
      } catch (error) {
        console.error('Token validation failed:', error);
        this.logout();
      }
    }
  }
  
  // 登录
  async login(email, password, captchaData = null) {
    try {
      const requestData = {
        email,
        password
      };
      
      // 如果提供了验证码数据，添加到请求中
      if (captchaData) {
        requestData.captcha_session_id = captchaData.captcha_session_id;
        requestData.captcha_code = captchaData.captcha_code;
      }
      
      const response = await apiClient.post(API_ENDPOINTS.AUTH.LOGIN, requestData);
      
      if (response.token) {
        apiClient.setToken(response.token);
        this.currentUser = response.user;
        this.isAuthenticated = true;
        this.userRole = response.user.role;
        
        // 通知状态变化
        setTimeout(() => notifyAuthStateChange(), 100);
        
        return response;
      } else {
        throw new Error('No token received');
      }
    } catch (error) {
      console.error('Login failed:', error);
      // 确保登录失败时清除状态
      this.logout();
      throw error;
    }
  }
  
  // 注册
  async register(email, password, captchaData = null) {
    try {
      const requestData = {
        email,
        password
      };
      
      // 如果提供了验证码数据，添加到请求中
      if (captchaData) {
        requestData.captcha_session_id = captchaData.captcha_session_id;
        requestData.captcha_code = captchaData.captcha_code;
      }
      
      const response = await apiClient.post(API_ENDPOINTS.AUTH.REGISTER, requestData);
      
      // 注册可能需要邮箱验证，不一定立即返回token
      if (response.token) {
        apiClient.setToken(response.token);
        this.currentUser = response.user;
        this.isAuthenticated = true;
        this.userRole = response.user.role;
        
        // 通知状态变化
        setTimeout(() => notifyAuthStateChange(), 100);
      }
      
      return response;
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  }
  
  // 登出
  async logout() {
    try {
      await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT);
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // 无论 API 调用是否成功，都清除本地状态
      apiClient.setToken(null);
      this.currentUser = null;
      this.isAuthenticated = false;
      this.userRole = 'user';
      
      // 通知状态变化
      setTimeout(() => notifyAuthStateChange(), 100);
    }
  }
  
  // 获取当前用户信息
  async getCurrentUser() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.AUTH.ME);
      
      this.currentUser = response.user;
      this.isAuthenticated = true;
      this.userRole = response.user.role;
      
      // 通知状态变化
      setTimeout(() => notifyAuthStateChange(), 100);
      
      return response.user;
    } catch (error) {
      console.error('Get current user failed:', error);
      this.logout();
      throw error;
    }
  }
  
  // 获取所有用户（管理员功能）
  async getUsers() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.AUTH.USERS);
      return response.users;
    } catch (error) {
      console.error('Get users failed:', error);
      throw error;
    }
  }
  
  // 检查是否为管理员
  isAdmin() {
    return this.userRole === 'admin';
  }
  
  // 检查是否已登录
  isLoggedIn() {
    return this.isAuthenticated && this.currentUser;
  }
  
  // 获取用户角色
  getUserRole() {
    return this.userRole;
  }
  
  // 获取当前用户
  getUser() {
    return this.currentUser;
  }
  
  // 修改密码
  async changePassword(newPassword) {
    try {
      const response = await apiClient.put(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, {
        newPassword
      });
      
      return response;
    } catch (error) {
      console.error('Change password failed:', error);
      throw error;
    }
  }
  
  // 验证邮箱
  async verifyEmail(email, verificationCode) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.VERIFY_EMAIL, {
        email,
        verification_code: verificationCode
      });
      
      if (response.token) {
        apiClient.setToken(response.token);
        this.currentUser = response.user;
        this.isAuthenticated = true;
        this.userRole = response.user.role;
        
        // 通知状态变化
        setTimeout(() => notifyAuthStateChange(), 100);
      }
      
      return response;
    } catch (error) {
      console.error('Email verification failed:', error);
      throw error;
    }
  }
  
  // 重新发送验证码
  async resendVerification(email) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.RESEND_VERIFICATION, {
        email
      });
      
      return response;
    } catch (error) {
      console.error('Resend verification failed:', error);
      throw error;
    }
  }
  
  // 忘记密码
  async forgotPassword(email) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, {
        email
      });
      
      return response;
    } catch (error) {
      console.error('Forgot password failed:', error);
      throw error;
    }
  }
  
  // 重置密码
  async resetPassword(token, newPassword) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
        token,
        new_password: newPassword
      });
      
      return response;
    } catch (error) {
      console.error('Reset password failed:', error);
      throw error;
    }
  }
}

// 创建全局认证服务实例
export const authService = new AuthService();

// 存储所有的认证状态监听器
const authStateListeners = [];

// 兼容 Firebase 的认证状态监听器
// eslint-disable-next-line no-unused-vars
export const onAuthStateChanged = (authObj, callback) => {
  // authObj 参数为了兼容 Firebase API，但在我们的实现中不使用
  // 检查参数
  if (typeof callback !== 'function') {
    console.error('onAuthStateChanged: callback must be a function');
    return () => {};
  }
  
  // 添加到监听器列表
  authStateListeners.push(callback);
  
  // 立即调用一次回调
  callback(authService.getUser());
  
  // 返回一个取消订阅的函数
  return () => {
    const index = authStateListeners.indexOf(callback);
    if (index > -1) {
      authStateListeners.splice(index, 1);
    }
    console.log('Auth state listener unsubscribed');
  };
};

// 通知所有监听器状态变化
const notifyAuthStateChange = () => {
  const user = authService.getUser();
  authStateListeners.forEach(callback => {
    try {
      callback(user);
    } catch (error) {
      console.error('Auth state callback error:', error);
    }
  });
};

// 兼容 Firebase 的登录函数
// eslint-disable-next-line no-unused-vars
export const signInWithEmailAndPassword = async (authObj, email, password) => {
  // authObj 参数为了兼容 Firebase API，但在我们的实现中不使用
  return authService.login(email, password);
};

// 兼容 Firebase 的注册函数
// eslint-disable-next-line no-unused-vars
export const createUserWithEmailAndPassword = async (authObj, email, password) => {
  // authObj 参数为了兼容 Firebase API，但在我们的实现中不使用
  return authService.register(email, password);
};

// 兼容 Firebase 的登出函数
// eslint-disable-next-line no-unused-vars
export const signOut = async (authObj) => {
  // authObj 参数为了兼容 Firebase API，但在我们的实现中不使用
  return authService.logout();
};

// 模拟 Firebase auth 对象
export const auth = {
  currentUser: null,
  // 添加其他需要的属性
};

// 更新 auth.currentUser，包含角色信息
setInterval(() => {
  const user = authService.getUser();
  if (user) {
    auth.currentUser = {
      ...user,
      role: authService.getUserRole()
    };
  } else {
    auth.currentUser = null;
  }
}, 1000);

export default authService; 